import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import type { ShellServices } from "./services";

/** Откуда файл пришёл, если он ещё не занесён в файлы документа. */
export type SourceOrigin = "email_attachment" | "comment_attachment";

/**
 * Файл из переписки или обсуждения, который ещё не стал файлом документа.
 *
 * Байты у него уже есть в хранилище и проверены; чтобы подписывать его и вести
 * версии, его достаточно добавить в файлы документа — без копирования.
 */
export interface SourceFile {
  readonly key: string;
  readonly origin: SourceOrigin;
  /** Объект хранилища: по нему источник узнаётся среди файлов документа. */
  readonly blobId: string;
  readonly name: string;
  readonly mimeType: string | null;
  readonly size: number | null;
  readonly sha256: string | null;
  /** `clean`, `pending`, `quarantined`. */
  readonly scanStatus: string;
  readonly messageId?: string;
  readonly attachmentId?: string;
  readonly commentId?: string;
}

/** Вложение сообщения обсуждения. */
export interface DiscussionFile {
  readonly fileId: string;
  readonly commentId: string;
}

/** Состояние объекта хранилища словами антивирусной проверки. */
function scanStatusOfRecord(status: string): string {
  if (status === "ready") return "clean";
  if (status === "pending") return "pending";

  return "quarantined";
}

/**
 * Файлы переписки и обсуждения документа.
 *
 * Читаются теми же операциями, что и вкладки «Переписка» и «Обсуждение», и
 * подчиняются тем же правам: если раздел человеку недоступен, файлов из него
 * не будет, а остальные покажутся как обычно.
 */
export function useDocumentFileSources({
  services,
  documentId,
  discussionFiles,
  enabled,
}: {
  services: ShellServices;
  documentId: string;
  discussionFiles: readonly DiscussionFile[];
  enabled: boolean;
}) {
  const messages = useQuery({
    queryKey: ["document-workflow", "documents", documentId, "mail-sources"],
    queryFn: async () =>
      (await services.documentWorkflow.messages(documentId)).data,
    enabled,
    retry: false,
  });
  const withFiles = useMemo(
    () => (messages.data ?? []).filter((message) => message.has_attachments),
    [messages.data],
  );
  const mailFiles = useQueries({
    queries: withFiles.map((message) => ({
      queryKey: [
        "document-workflow",
        "mail-message",
        message.id,
        "attachments",
      ],
      queryFn: () => services.documentWorkflow.attachments(message.id),
      retry: false,
    })),
  });
  const records = useQueries({
    queries: discussionFiles.map((item) => ({
      queryKey: ["files", item.fileId],
      queryFn: () => services.files.get!(item.fileId),
      enabled: enabled && services.files.get !== undefined,
      retry: false,
    })),
  });

  const mailData = mailFiles.map((query) => query.data);
  const recordData = records.map((query) => query.data);
  const sources = ((): readonly SourceFile[] => {
    const result: SourceFile[] = [];
    withFiles.forEach((message, index) => {
      for (const attachment of mailData[index] ?? []) {
        result.push({
          key: `mail:${attachment.id}`,
          origin: "email_attachment",
          blobId: attachment.file_id,
          name: attachment.name,
          mimeType: attachment.mime_type,
          size: attachment.size,
          sha256: attachment.sha256,
          scanStatus: attachment.scan_status,
          messageId: message.id,
          attachmentId: attachment.id,
        });
      }
    });
    discussionFiles.forEach((item, index) => {
      const record = recordData[index]?.data;
      if (record === undefined) return;
      result.push({
        key: `comment:${item.fileId}`,
        origin: "comment_attachment",
        blobId: record.id,
        name: record.filename,
        mimeType: record.mime_type,
        size: record.size,
        sha256: record.sha256,
        scanStatus: scanStatusOfRecord(record.status),
        commentId: item.commentId,
      });
    });

    return result;
  })();

  const pending =
    enabled &&
    (messages.isLoading ||
      mailFiles.some((query) => query.isLoading) ||
      records.some((query) => query.isLoading));
  const mailFailed =
    messages.isError || mailFiles.some((query) => query.isError);
  const discussionFailed = records.some((query) => query.isError);

  return {
    sources,
    messages: messages.data ?? [],
    pending,
    mailFailed,
    discussionFailed,
    retry: () => {
      void messages.refetch();
      mailFiles.forEach((query) => void query.refetch());
      records.forEach((query) => void query.refetch());
    },
  };
}
